import { useQuery } from '@tanstack/react-query';
import { workspaceService } from '../services/workspaceService';
import { usePageVisibility } from '@/modules/shared/hooks/usePageVisibility';
import type {
  WorkspaceOverviewRequestDto,
  WorkspaceSummaryDto,
  WorkspaceListDto,
  WorkspaceHospitalNodeDto,
  WorkspaceFilterStateDto,
} from '../types/workspace';

// ─────────────────────────────────────────────────────────────
// Auto-refresh interval (ms)
// ─────────────────────────────────────────────────────────────

const REFETCH_INTERVAL_MS = 30_000;

// ─────────────────────────────────────────────────────────────
// Return type
// ─────────────────────────────────────────────────────────────

export interface UseWorkspaceOverviewResult {
  /** Filter-scoped KPI summary counts. Always consistent with the list. */
  summary: WorkspaceSummaryDto | null;
  /** Paginated entity list. */
  list: WorkspaceListDto<WorkspaceHospitalNodeDto> | null;
  /** Lightweight filter state metadata. */
  filterState: WorkspaceFilterStateDto | null;
  /** UTC timestamp from the server indicating when this data was generated. */
  lastUpdatedUtc: string | null;
  /** True during the very first load (no data yet). Show skeletons. */
  isLoading: boolean;
  /** True on any subsequent background refresh (data is already present). */
  isRefetching: boolean;
  /** True when the query is in an error state. */
  isError: boolean;
  /** Error object if present. */
  error: Error | null;
  /** Trigger an immediate manual refetch. */
  refetch: () => void;
  /** React Query dataUpdatedAt timestamp (ms since epoch). */
  dataUpdatedAt: number;
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

interface UseWorkspaceOverviewOptions {
  /** Disable the query entirely (e.g. waiting for workspace init). */
  enabled?: boolean;
}

/**
 * Unified workspace overview hook.
 *
 * Calls a single POST /workspace/overview endpoint that returns both
 * filter-scoped KPI summary counts and the paginated entity list.
 *
 * Features:
 * - 30-second controlled auto-refresh (paused when tab is hidden)
 * - refetchOnWindowFocus for immediate updates on tab return
 * - No duplicate in-flight requests (React Query deduplication)
 * - Does not reset pagination or filter state on background refresh
 */
export function useWorkspaceOverview(
  programId: string,
  filters: WorkspaceOverviewRequestDto,
  options: UseWorkspaceOverviewOptions = {}
): UseWorkspaceOverviewResult {
  const { enabled = true } = options;
  const isVisible = usePageVisibility();

  const query = useQuery({
    queryKey: ['workspace-overview', programId, filters] as const,
    queryFn: () => workspaceService.getOverview(programId, filters),
    enabled: !!programId && enabled,
    // Pause interval when tab is not visible to avoid unnecessary network calls
    refetchInterval: isVisible ? REFETCH_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
    // Data is considered fresh for just under the refetch interval
    staleTime: REFETCH_INTERVAL_MS - 1_000,
    // Keep previous data while refetching so the UI never goes blank
    placeholderData: (previousData) => previousData,
  });

  const data = query.data?.success ? query.data.data : null;

  return {
    summary: data?.summary ?? null,
    list: data?.list ?? null,
    filterState: data?.filters ?? null,
    lastUpdatedUtc: data?.lastUpdatedUtc ?? null,
    // isLoading is true only on the very first fetch (no data yet)
    isLoading: query.isLoading,
    // isRefetching is true on background refetches when data already exists
    isRefetching: query.isFetching && !query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
